import { categoryLabel } from '@/modules/lab/catalog/catalogTaxonomy'
import type { MatchCandidate } from '@/modules/lab/catalog/catalogDedup'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

type Props = {
  matches: MatchCandidate[]
  checking: boolean
  queryLength: number
  canOverride: boolean
  onEnableExisting: (match: MatchCandidate) => void
  onCreateLocalVariant: () => void
  onContinueAnyway?: () => void
}

function basisLabel(basis: string): string {
  if (basis === 'CODE') return 'Code match'
  if (basis === 'SYNONYM') return 'Alias match'
  if (basis === 'NAME') return 'Name match'
  if (basis === 'TRIGRAM') return 'Similar spelling'
  return 'Similar'
}

function provenanceLabel(match: MatchCandidate): string {
  if (match.isLocal) return 'Hospital-only'
  if (match.isDraft) return 'Draft'
  return 'Standard'
}

export function InlineDedupSuggestions({
  matches,
  checking,
  queryLength,
  canOverride,
  onEnableExisting,
  onCreateLocalVariant,
  onContinueAnyway,
}: Props) {
  const strong = matches.some((m) => m.score >= 0.92)
  const severity = strong ? 'warning' : matches.length > 0 ? 'info' : 'success'

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        bgcolor: 'background.paper',
        borderColor: strong ? 'warning.main' : 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">
          Catalog matches
        </Typography>
        {checking && <CircularProgress size={14} />}
      </Stack>

      {checking && queryLength > 0 && (
        <Typography variant="caption" color="text.secondary">
          Checking existing tests…
        </Typography>
      )}

      {!checking && queryLength === 0 && (
        <Typography variant="caption" color="text.secondary">
          Type a name or code to see matches (e.g. CBC, SGOT).
        </Typography>
      )}

      {!checking && queryLength > 0 && matches.length === 0 && (
        <Alert severity="success" sx={{ py: 0.5 }}>
          No close matches — safe to add as new local test.
        </Alert>
      )}

      {matches.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {matches.map((match) => (
            <Box
              key={match.masterId}
              sx={{
                p: 1.25,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {match.name}
                  </Typography>
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary" display="block">
                    {match.code}
                  </Typography>
                  {(match.aliases?.length ?? 0) > 0 && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      Also: {match.aliases.slice(0, 3).join(', ')}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {categoryLabel(match.category ?? '')}
                    {match.specimenType ? ` · ${match.specimenType}` : ''}
                  </Typography>
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Chip
                    size="small"
                    label={`${Math.round(match.score * 100)}%`}
                    color={match.score >= 0.92 ? 'warning' : 'default'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {basisLabel(match.basis)}
                  </Typography>
                  <Chip size="small" label={provenanceLabel(match)} variant="outlined" />
                </Stack>
              </Stack>
              <Box sx={{ mt: 1 }}>
                {match.hospitalEnabled ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <LocalHospitalOutlinedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      Enabled at hospital
                    </Typography>
                  </Stack>
                ) : (
                  <Button size="small" variant="contained" fullWidth onClick={() => onEnableExisting(match)}>
                    Enable at hospital
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {matches.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button size="small" variant="outlined" onClick={onCreateLocalVariant}>
            Create hospital-only variant
          </Button>
          {canOverride && onContinueAnyway && strong && (
            <Button size="small" color="inherit" onClick={onContinueAnyway}>
              Continue anyway
            </Button>
          )}
        </Stack>
      )}

      {!checking && queryLength > 0 && matches.length > 0 && (
        <Alert severity={severity} sx={{ mt: 1, py: 0 }}>
          Review similar tests before creating a duplicate.
        </Alert>
      )}
    </Paper>
  )
}
